import * as THREE from 'three';

export type SegmentCylinderHit = {
  /** Distance along segment from base to first intersection, in [0, segmentLength]. */
  tAlongSegment: number;
  /** World-space intersection point. */
  point: THREE.Vector3;
  /** Y in cylinder local space (0 = base, pellHeightM = top). */
  localY: number;
};

/**
 * Intersect a line segment (blade base -> tip) against a vertical cylinder
 * (axis along world +Y, base at cylinderBase, height pellHeightM, radius).
 *
 * Returns the first hit along the segment (smallest t), or null if no intersection.
 *
 * Pure function: no side effects, no allocations beyond returned object.
 */
export function intersectSegmentCylinder(
  segBase: THREE.Vector3,
  segTip: THREE.Vector3,
  cylinderBase: THREE.Vector3,
  pellHeightM: number,
  radiusM: number,
): SegmentCylinderHit | null {
  if (!finite3(segBase) || !finite3(segTip) || !finite3(cylinderBase)) return null;
  if (!isFinite(pellHeightM) || !isFinite(radiusM) || pellHeightM <= 0 || radiusM <= 0) return null;

  const dx = segTip.x - segBase.x;
  const dz = segTip.z - segBase.z;
  const ox = segBase.x - cylinderBase.x;
  const oz = segBase.z - cylinderBase.z;

  const a = dx * dx + dz * dz;
  const b = 2 * (ox * dx + oz * dz);
  const c = ox * ox + oz * oz - radiusM * radiusM;

  let tHit: number | null = null;

  if (a > 1e-8) {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const sqrtDisc = Math.sqrt(disc);
      const t1 = (-b - sqrtDisc) / (2 * a);
      const t2 = (-b + sqrtDisc) / (2 * a);
      for (const t of [t1, t2]) {
        if (t < 0 || t > 1) continue;
        const y = segBase.y + (segTip.y - segBase.y) * t;
        const localY = y - cylinderBase.y;
        if (localY < 0 || localY > pellHeightM) continue;
        if (tHit === null || t < tHit) tHit = t;
      }
    }
  } else {
    if (c <= 0) {
      const y0 = segBase.y - cylinderBase.y;
      const y1 = segTip.y - cylinderBase.y;
      if ((y0 >= 0 && y0 <= pellHeightM) || (y1 >= 0 && y1 <= pellHeightM)) {
        tHit = 0;
      }
    }
  }

  if (tHit === null) return null;

  const segLen = Math.hypot(segTip.x - segBase.x, segTip.y - segBase.y, segTip.z - segBase.z);
  const point = new THREE.Vector3(
    segBase.x + (segTip.x - segBase.x) * tHit,
    segBase.y + (segTip.y - segBase.y) * tHit,
    segBase.z + (segTip.z - segBase.z) * tHit,
  );
  return {
    tAlongSegment: tHit * segLen,
    point,
    localY: point.y - cylinderBase.y,
  };
}

/**
 * Whether a world-space point lies inside the closed cylinder volume
 * (axis along world +Y, base at cylinderBase, height pellHeightM, radius).
 */
export function pointInsideCylinder(
  p: THREE.Vector3,
  cylinderBase: THREE.Vector3,
  pellHeightM: number,
  radiusM: number,
): boolean {
  if (!finite3(p) || !finite3(cylinderBase)) return false;
  if (!isFinite(pellHeightM) || !isFinite(radiusM) || pellHeightM <= 0 || radiusM <= 0)
    return false;
  const localY = p.y - cylinderBase.y;
  if (localY < 0 || localY > pellHeightM) return false;
  const dx = p.x - cylinderBase.x;
  const dz = p.z - cylinderBase.z;
  return dx * dx + dz * dz <= radiusM * radiusM;
}

function finite3(v: THREE.Vector3): boolean {
  return isFinite(v.x) && isFinite(v.y) && isFinite(v.z);
}

export type SegmentSphereHit = {
  /** Closest point on the segment to the sphere center. */
  point: THREE.Vector3;
  /** Distance from the sphere center to that closest point. */
  distanceFromCenter: number;
};

/**
 * Closest-approach test of a line segment against a sphere (center + radius).
 * Orientation-independent — robust for "thrust the tip into the ball at the center".
 *
 * Returns the closest point on the segment to `center` and its distance when that
 * distance is within `radiusM`; otherwise null. Pure function.
 */
export function intersectSegmentSphere(
  segStart: THREE.Vector3,
  segEnd: THREE.Vector3,
  center: THREE.Vector3,
  radiusM: number,
): SegmentSphereHit | null {
  if (!finite3(segStart) || !finite3(segEnd) || !finite3(center)) return null;
  if (!isFinite(radiusM) || radiusM <= 0) return null;

  const abx = segEnd.x - segStart.x;
  const aby = segEnd.y - segStart.y;
  const abz = segEnd.z - segStart.z;
  const lenSq = abx * abx + aby * aby + abz * abz;

  let t = 0;
  if (lenSq > 1e-12) {
    t =
      ((center.x - segStart.x) * abx +
        (center.y - segStart.y) * aby +
        (center.z - segStart.z) * abz) /
      lenSq;
    t = Math.min(1, Math.max(0, t));
  }

  const cx = segStart.x + abx * t;
  const cy = segStart.y + aby * t;
  const cz = segStart.z + abz * t;
  const dx = cx - center.x;
  const dy = cy - center.y;
  const dz = cz - center.z;
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (dist > radiusM) return null;

  return { point: new THREE.Vector3(cx, cy, cz), distanceFromCenter: dist };
}
